import { useState, type KeyboardEvent } from 'react';
import { Box, Chip, FormHelperText, Stack, TextField, Typography } from '@mui/material';
import { useField } from 'formik';

const MAX_TAGS_LENGTH = 255;

const parseTags = (value?: string): string[] =>
  (value ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

interface FormikTagsInputProps {
  name: string;
  label?: string;
  placeholder?: string;
}

/**
 * Tag editor backed by a comma-separated string in Formik.
 * The text field stays empty for entering new tags; existing tags render as
 * deletable chips below it. The joined string is capped at 255 characters.
 */
export const FormikTagsInput = ({
  name,
  label = 'Tags',
  placeholder = 'Type a tag and press Enter',
}: FormikTagsInputProps) => {
  const [field, meta, helpers] = useField<string>(name);
  const [draft, setDraft] = useState('');

  const tags = parseTags(field.value);
  const hasError = meta.touched && Boolean(meta.error);

  const commit = (nextTags: string[]) => {
    helpers.setValue(nextTags.join(','));
    helpers.setTouched(true, false);
  };

  const addTag = (raw: string) => {
    const candidate = raw.trim();
    if (!candidate) return;
    if (tags.includes(candidate)) {
      setDraft('');
      return;
    }
    const next = [...tags, candidate];
    if (next.join(',').length > MAX_TAGS_LENGTH) {
      helpers.setError(`Tags cannot exceed ${MAX_TAGS_LENGTH} characters`);
      return;
    }
    commit(next);
    setDraft('');
  };

  const removeTag = (tag: string) => {
    commit(tags.filter((t) => t !== tag));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag(draft);
    } else if (event.key === 'Backspace' && !draft && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const remaining = MAX_TAGS_LENGTH - (field.value?.length ?? 0);

  return (
    <Box>
      <TextField
        variant="filled"
        label={label}
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => addTag(draft)}
        error={hasError}
        fullWidth
        sx={{
          '& .MuiInputBase-input': { fontSize: '16px' },
          '& .MuiFormLabel-root': { fontSize: '14px' },
        }}
      />
      {hasError ? (
        <FormHelperText error>{meta.error}</FormHelperText>
      ) : (
        <FormHelperText>{`${remaining} characters remaining`}</FormHelperText>
      )}
      {tags.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Existing tags
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" onDelete={() => removeTag(tag)} />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
